// src/utilities/pendingSignup.js — SOUND-715
//
// Where a half-finished signup parks its business name.
//
// Provisioning is deferred until the person confirms their email address, and
// the API requires `companyName` on the self-serve path. By the time they click
// the link the form state is gone — and if they come back in a fresh tab, or
// days later via sign-in, there is no form at all. This is the only thing that
// carries the name across that gap.
//
// Extracted from auth.js on purpose. sound-app has no component-test harness
// (SOUND-679), so logic that lives inside a component is logic nothing checks;
// the uid guard below is exactly the kind of quiet mistake that class of gap
// lets through. Everything here is pure apart from the storage call, and every
// storage call is wrapped: Safari private mode throws on setItem, and a signup
// must not die because a convenience cache is unavailable.

export const PENDING_SIGNUP_KEY = "sound.pendingSignup";

/** @param {string} uid  @param {string} [companyName] */
export function rememberPendingSignup(uid, companyName) {
  if (!uid) return;
  try {
    window.localStorage.setItem(
      PENDING_SIGNUP_KEY,
      JSON.stringify({ uid, companyName: companyName ?? "" })
    );
  } catch {
    // Private mode, or storage disabled. The panel asks for the name instead.
  }
}

/**
 * Read the stash, but ONLY for the account that wrote it.
 *
 * The uid check is the point of this function. Two people signing up on one
 * machine — a shared laptop, a demo, someone testing — would otherwise have the
 * second signup adopt the first one's business name, silently and permanently,
 * because a company name is never shown again after the wizard.
 *
 * @returns {{uid: string, companyName: string}|null}
 */
export function readPendingSignup(uid) {
  if (!uid) return null;
  try {
    const raw = window.localStorage.getItem(PENDING_SIGNUP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.uid === uid ? parsed : null;
  } catch {
    // Absent, unparseable, or unreadable — all mean "nothing remembered".
    return null;
  }
}

export function clearPendingSignup() {
  try {
    window.localStorage.removeItem(PENDING_SIGNUP_KEY);
  } catch {
    // Nothing to clear.
  }
}

/**
 * The business name to send to POST /users, from every place it might live.
 *
 * SOUND-726. `provisionAccount` did this inline as
 * `(companyName ?? remembered?.companyName ?? "").trim()`, and `??` only falls
 * through on null/undefined — so an empty string from a rendered-but-untouched
 * input BEAT a perfectly good remembered name. That is a silent downgrade of the
 * one field whose absence used to delete the account.
 *
 * Trim first, then take the first non-empty. Whitespace is not a name; the API
 * agrees and refuses it.
 *
 * @param {{typed?: string, remembered?: string}} sources
 * @returns {string} the name, or "" if we genuinely do not have one
 */
export function resolveCompanyName({ typed, remembered } = {}) {
  return (
    [typed, remembered]
      .map((v) => String(v ?? "").trim())
      .find((v) => v.length > 0) || ""
  );
}

/**
 * Whether the confirmation panel has to ASK for the business name.
 *
 * The name is stashed here because provisioning happens AFTER the person
 * confirms their email — possibly on a different device. Confirming on a phone,
 * in another browser, in private mode (where setItem throws), or after clearing
 * storage all arrive with nothing. So does a first Google sign-in, which never
 * had a form to type it into.
 *
 * Until 2026-08-28 that state DELETED their Firebase account. The API refuses
 * with COMPANY_NAME_REQUIRED now and keeps the account, and this is what decides
 * to put the field in front of them.
 *
 * @param {{typed?: string, remembered?: string}} sources
 * @returns {boolean}
 */
export function needsCompanyName(sources) {
  return resolveCompanyName(sources).length === 0;
}
